import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { partition } from "lodash";
import moment from "moment-timezone";
import { useMemo } from "react";
import { SINGLE_DAY_DATE_FORMAT } from "../monthly-view.types";
import findOrAddRowWithRoomForEvent from "../utils/find-or-add-row-with-room-for-event";
import getMonthlyViewWeeks from "../utils/get-monthly-view-weeks";
import { isFullDay } from "../utils/is-full-day";
import useNormalizedEvents from "./use-normalized-events";
import useNormalizedNewSessionPlaceholder from "./use-normalized-new-session-placeholder";

const useMonthlyViewEventSlots = () => {
  const { timeRange, currentCalendarTimezone } = useCalendarTime();
  const normalizedEvents = useNormalizedEvents(true);
  const sessionPlaceholder = useNormalizedNewSessionPlaceholder();

  const [fullDayEvents, shortEvents] = useMemo(
    () =>
      partition(
        normalizedEvents,
        (event) =>
          ["blockedInterval", "gcalEvent"].includes(event.type) &&
          isFullDay(event.startISO, event.endISO),
      ),
    [normalizedEvents],
  );

  const monthlyViewEventSlots = useMemo(() => {
    const { weeksInMonth, weekIndexDictionary } = getMonthlyViewWeeks(
      timeRange,
      currentCalendarTimezone,
    );

    const fullDayEventsPrecededByPlaceholder = sessionPlaceholder
      ? [sessionPlaceholder, ...fullDayEvents]
      : fullDayEvents;

    fullDayEventsPrecededByPlaceholder.forEach((event) => {
      const { startISO, endISO } = event;
      const startDate = moment.tz(startISO, currentCalendarTimezone);
      const endDate = moment
        .tz(endISO, currentCalendarTimezone)
        .subtract(1, "second");

      let startWeekIndex: number;
      let startDayIndex: number;

      let endWeekIndex: number;
      let endDayIndex: number;

      const startEntry =
        weekIndexDictionary[startDate.format(SINGLE_DAY_DATE_FORMAT)];
      const endEntry =
        weekIndexDictionary[endDate.format(SINGLE_DAY_DATE_FORMAT)];

      if (startEntry) {
        startWeekIndex = startEntry.weekIndex;
        startDayIndex = startEntry.dayIndex;
      } else {
        // starts before the time range
        startWeekIndex = 0;
        startDayIndex = 0;
      }

      if (endEntry) {
        endWeekIndex = endEntry.weekIndex;
        endDayIndex = endEntry.dayIndex;
      } else {
        // ends after the time range
        endWeekIndex = weeksInMonth.length - 1;
        endDayIndex = 6;
      }

      if (!startEntry && !endEntry) {
        return;
      }

      for (
        let currentWeekIndex = startWeekIndex;
        currentWeekIndex <= endWeekIndex;
        currentWeekIndex++
      ) {
        const week = weeksInMonth[currentWeekIndex];

        const firstDayInWeekIndex =
          currentWeekIndex === startWeekIndex ? startDayIndex : 0;
        const lastDayInWeekIndex =
          currentWeekIndex === endWeekIndex ? endDayIndex : 6;

        const lengthInDaysWithinWeek =
          lastDayInWeekIndex - firstDayInWeekIndex + 1;

        const firstFreeRowIndex = findOrAddRowWithRoomForEvent(
          week,
          firstDayInWeekIndex,
          lengthInDaysWithinWeek,
        );

        for (
          let currentDayIndex = firstDayInWeekIndex;
          currentDayIndex <= lastDayInWeekIndex;
          currentDayIndex++
        ) {
          const currentDay = week[currentDayIndex];

          if (currentDayIndex === firstDayInWeekIndex) {
            currentDay.slots[firstFreeRowIndex] = {
              type: "EVENT_START",
              event,
              daySpan: lengthInDaysWithinWeek,
              key: event.id,
            };
          } else {
            currentDay.slots[firstFreeRowIndex] = {
              type: "EVENT_CONTINUED",
              key: event.id,
            };
          }
        }
      }
    });

    shortEvents.forEach((event) => {
      const { startISO } = event;
      const startDate = moment.tz(startISO, currentCalendarTimezone);

      const startEntry =
        weekIndexDictionary[startDate.format(SINGLE_DAY_DATE_FORMAT)];
      if (!startEntry) {
        return;
      }

      const { weekIndex, dayIndex } = startEntry;
      const week = weeksInMonth[weekIndex];
      const day = week[dayIndex];
      const firstFreeRowIndex = findOrAddRowWithRoomForEvent(week, dayIndex, 1);
      day.slots[firstFreeRowIndex] = {
        type: "SHORT_EVENT",
        event,
        key: event.id,
      };
    });

    weeksInMonth.forEach((week) =>
      week.forEach((day) => {
        while (
          day.slots.length &&
          day.slots[day.slots.length - 1].type === "EMPTY"
        ) {
          day.slots.pop();
        }
      }),
    );

    return weeksInMonth;
  }, [
    fullDayEvents,
    shortEvents,
    timeRange,
    currentCalendarTimezone,
    sessionPlaceholder,
  ]);

  return monthlyViewEventSlots;
};

export default useMonthlyViewEventSlots;
